import * as React from 'react';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { $getSelection, $isRangeSelection, $setSelection } from 'lexical';
import type { InputRef } from 'antd';
import type { LexicalEditor, RangeSelection } from 'lexical';
import { isValidTableDimension } from './constants';
import type { ToolbarTableController } from './types';

export function useToolbarTable(
  editor: LexicalEditor
): ToolbarTableController {
  const [isEditorVisible, setIsEditorVisible] = React.useState(false);
  const [columnsValue, setColumnsValue] = React.useState('3');
  const [rowsValue, setRowsValue] = React.useState('3');
  const savedSelectionRef = React.useRef<RangeSelection | null>(null);
  const inputRef = React.useRef<InputRef>(null);

  React.useEffect(() => {
    if (!isEditorVisible) {
      return;
    }

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditorVisible]);

  const closeEditor = React.useCallback(() => {
    setIsEditorVisible(false);
    savedSelectionRef.current = null;
    setColumnsValue('3');
    setRowsValue('3');
  }, []);

  const openEditor = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        savedSelectionRef.current = selection.clone();
      } else {
        savedSelectionRef.current = null;
      }
    });

    setIsEditorVisible(true);
  }, [editor]);

  const insert = React.useCallback(() => {
    if (
      !isValidTableDimension(rowsValue, 500) ||
      !isValidTableDimension(columnsValue, 50)
    ) {
      return;
    }

    editor.update(() => {
      if (savedSelectionRef.current) {
        $setSelection(savedSelectionRef.current);
      }
    });

    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: columnsValue.trim(),
      rows: rowsValue.trim(),
    });

    closeEditor();
  }, [closeEditor, columnsValue, editor, rowsValue]);

  return {
    closeEditor,
    columnsValue,
    inputRef,
    insert,
    isEditorVisible,
    openEditor,
    rowsValue,
    setColumnsValue,
    setRowsValue,
  };
}
